/**
 * Ficha de uma célula do modelo CAMS.
 *
 * O selo "estimativa de modelo" vem antes de qualquer número: o CAMS global
 * tem células de ~40 km e não conhece a fumaça da queimada de ontem no
 * município vizinho como um sensor conhece. Em 23/09/2026 ele dava ~45 µg/m³
 * em Rio Branco enquanto os sensores mediam 5–15.
 */
import { ExternalLink } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { formatDateTimeBoth } from '../lib/time';
import { shownTimeOf, useTimeStore } from '../state/time';
import { CAMS_ATRIBUICAO, CAMS_URL, OPEN_METEO_URL, instantesHorarios } from './api';
import { corDaFaixa, faixaPm25, nomeDaFaixa, pmTexto, referenciasUgm3 } from './faixas';
import { Grafico } from './Grafico';
import { useArStore } from './store';
import { carregarSeries } from './sync';
import { horaDoModelo, indiceQueContem } from './tempo';
import type { VariavelCams } from './types';
import { HOUR_MS } from '../lib/time';
import { ProximosDiasAqui } from '../previsao/ProximosDiasAqui';

interface DefVar {
  nome: string;
  unidade: string;
  casas: number;
  piso: number;
}

const VARS: Readonly<Record<VariavelCams, DefVar>> = {
  pm2_5: { nome: 'MP2,5', unidade: 'µg/m³', casas: 1, piso: 30 },
  pm10: { nome: 'MP10', unidade: 'µg/m³', casas: 1, piso: 30 },
  carbon_monoxide: { nome: 'CO', unidade: 'µg/m³', casas: 0, piso: 200 },
  ozone: { nome: 'O₃', unidade: 'µg/m³', casas: 0, piso: 30 },
  nitrogen_dioxide: { nome: 'NO₂', unidade: 'µg/m³', casas: 1, piso: 5 },
  aerosol_optical_depth: { nome: 'AOD', unidade: '', casas: 2, piso: 0.3 },
};

const ORDEM: readonly VariavelCams[] = ['pm2_5', 'pm10', 'carbon_monoxide', 'ozone', 'nitrogen_dioxide', 'aerosol_optical_depth'];

const REF_PM25 = referenciasUgm3('conama');

function valorTexto(v: number | null | undefined, d: DefVar): string | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return `${v.toFixed(d.casas).replace('.', ',')}${d.unidade ? ` ${d.unidade}` : ''}`;
}

export function CamsPopup({ indice }: { indice: number }) {
  const cams = useArStore((s) => s.cams);
  const series = useArStore((s) => s.series);
  const situacaoSeries = useArStore((s) => s.situacaoSeries);
  const instante = useTimeStore((s) => shownTimeOf(s));
  const [variavel, setVariavel] = useState<VariavelCams>('pm2_5');

  useEffect(() => {
    void carregarSeries();
  }, []);

  // Enquanto a ficha está aberta, o mapa contorna a célula a que ela se refere.
  useEffect(() => {
    useArStore.setState({ celulaAberta: indice });
    return () => {
      if (useArStore.getState().celulaAberta === indice) useArStore.setState({ celulaAberta: null });
    };
  }, [indice]);

  const celula = cams?.celulas[indice];
  const pm = cams ? horaDoModelo(cams.pm25[indice], cams.h0, instante) : null;
  const faixa = faixaPm25(pm?.valor);
  const cor = faixa ? corDaFaixa(faixa) : '#c3ccda';

  const serieSel = variavel === 'pm2_5' ? cams?.pm25[indice] : series?.series[variavel]?.[indice];
  const h0 = variavel === 'pm2_5' ? (cams?.h0 ?? 0) : (series?.h0 ?? 0);
  const n = serieSel?.length ?? 0;
  const instantes = useMemo(() => instantesHorarios(h0, n), [h0, n]);
  const def = VARS[variavel];
  const linhas = useMemo(() => (serieSel ? [{ valores: serieSel, cor: variavel === 'pm2_5' ? cor : '#7fe3ff' }] : []), [serieSel, variavel, cor]);
  const iSeries = series ? indiceQueContem(series.horas, series.h0, HOUR_MS, instante) : -1;

  if (!cams || !celula) return null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-ink-400">
        <span className="inline-block size-2.5 rounded-[2px] opacity-70" style={{ background: cor }} aria-hidden />
        Qualidade do ar · modelo CAMS
      </div>
      <div className="inline-block rounded-md border border-alerta-400/50 bg-alerta-400/10 px-1.5 py-0.5 text-[10px] font-medium text-alerta-200">
        estimativa de modelo, não medição
      </div>

      <div className="leading-tight font-semibold text-white">
        Célula de {String(cams.passo).replace('.', ',')}° em {Math.abs(celula.lat).toFixed(1).replace('.', ',')}°{celula.lat < 0 ? 'S' : 'N'},{' '}
        {Math.abs(celula.lon).toFixed(1).replace('.', ',')}°{celula.lon < 0 ? 'O' : 'L'}
      </div>
      <ProximosDiasAqui lon={celula.lon} lat={celula.lat} />

      {pm ? (
        <div>
          <div className="flex items-baseline gap-2 pt-0.5">
            <span className="text-xl font-semibold tabular-nums" style={{ color: cor }}>
              {pmTexto(pm.valor)} µg/m³
            </span>
            {faixa && (
              <span className="text-xs" style={{ color: cor }}>
                {nomeDaFaixa(faixa)}
              </span>
            )}
          </div>
          <div className="text-[10px] text-ink-400">escala CONAMA 491 (o modelo não classifica)</div>
          <div className="text-[10px] text-ink-300 tabular-nums">hora do modelo: {formatDateTimeBoth(pm.t)}</div>
        </div>
      ) : (
        <div className="text-[11px] text-ink-300">O modelo não tem valor para a hora do quadro exibido.</div>
      )}

      <div className="flex flex-wrap gap-1 pt-1" role="group" aria-label="Variável do gráfico">
        {ORDEM.map((v) => {
          const disponivel = v === 'pm2_5' || !!series?.series[v];
          return (
            <button
              key={v}
              type="button"
              disabled={!disponivel}
              aria-pressed={variavel === v}
              onClick={() => setVariavel(v)}
              className={`min-h-7 rounded-md px-1.5 text-[11px] pointer-coarse:min-h-11 pointer-coarse:px-2.5 ${
                variavel === v ? 'bg-white/15 text-white' : 'text-ink-300 hover:bg-white/10'
              } disabled:opacity-40`}
            >
              {VARS[v].nome}
            </button>
          );
        })}
      </div>

      {serieSel ? (
        <Grafico
          instantes={instantes}
          iAtual={indiceQueContem(n, h0, HOUR_MS, instante)}
          linhas={linhas}
          referencias={variavel === 'pm2_5' ? REF_PM25 : undefined}
          piso={def.piso}
          rotulo={`${def.nome} do modelo CAMS nas últimas 48 horas`}
          valor={(i) => valorTexto(serieSel[i], def)}
          resumo={def.unidade || 'adimensional'}
        />
      ) : (
        <div className="text-[11px] text-ink-400">
          {situacaoSeries.estado === 'error' ? `As séries do modelo não carregaram: ${situacaoSeries.erro}` : 'Baixando as séries do modelo…'}
        </div>
      )}

      {series && iSeries >= 0 && (
        <div className="grid grid-cols-3 gap-1 border-t border-white/10 pt-1.5">
          {ORDEM.filter((v) => v !== 'pm2_5').map((v) => (
            <div key={v} className="rounded-md bg-white/5 px-1.5 py-1">
              <div className="text-[10px] text-ink-400" title={v === 'aerosol_optical_depth' ? 'Profundidade óptica dos aerossóis (adimensional)' : undefined}>
                {VARS[v].nome}
              </div>
              <div className="text-[11px] text-ink-100 tabular-nums">{valorTexto(series.series[v]?.[indice]?.[iSeries], VARS[v]) ?? '—'}</div>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-white/10 pt-1 text-[10px] leading-snug text-ink-400">
        {CAMS_ATRIBUICAO}. <a href={CAMS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
          Copernicus
          <ExternalLink size={10} />
        </a>{' '}
        via{' '}
        <a href={OPEN_METEO_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
          Open-Meteo
          <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}
